/**
 * DELIVERY GUARANTEE WIDGET
 *
 * Explains why RiiRoo delivers ride-on toys BEFORE Christmas
 * Addresses concern: "How do I hide a massive ride-on car from my kids?"
 *
 * Messaging Strategy:
 * - Turn "no post-Christmas delivery" into competitive advantage
 * - Focus on peace of mind, quality checks, time to fix issues
 * - Provide practical storage solutions for LARGE ride-on toys
 *
 * Design: Follows FAQ/Support widget pattern, black/white/muted tones
 */

(function(window) {
  'use strict';

  class DeliveryGuaranteeWidget extends window.ProductWidgets.BaseWidget {
    constructor(manager, container, config) {
      super(manager, container, config);

      this.modalId = `delivery-guarantee-modal-${config.id}`;

      // Content (customisable via block settings)
      this.content = {
        cardHeading: 'Peace of Mind Delivery',
        cardSubheading: 'Delivered before Christmas for quality assurance',
        viewButton: 'Why This Matters',
        modalHeading: 'Peace of Mind Delivery',
        modalSubheading: 'Why we deliver before Christmas - and how to keep it secret'
      };
    }

    /**
     * Initialize widget
     */
    async init() {
      // No async data needed - static content widget
    }

    /**
     * Render widget to container
     */
    render() {
      const html = `
        ${this.buildCardHTML()}
        ${this.buildModalHTML()}
      `;

      this.setContent(html);
      this.attachEventListeners();
      this.emit('delivery-guarantee:rendered');
    }

    /**
     * Build widget card HTML (follows FAQ/Support pattern)
     */
    buildCardHTML() {
      return `
        <div class="pw-widget pw-delivery-guarantee-widget" style="width: 100%;">
          <div class="pw-widget--card pw-widget--card-clickable" style="padding: 16px; cursor: pointer;"
               data-modal-trigger="${this.modalId}">

            <!-- Header with Icon -->
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-base-button) / 0.1); border-radius: var(--rounded-button);">
                ${this.getDeliveryIconSVG()}
              </div>
              <div style="flex: 1;">
                <h3 class="pw-text-lg pw-font-bold" style="margin: 0;">${this.content.cardHeading}</h3>
                <p class="pw-text-sm" style="margin: 0; color: rgb(var(--color-drawer-text) / 0.7);">
                  ${this.content.cardSubheading}
                </p>
              </div>
            </div>

            <!-- Benefits Badge -->
            <div style="margin-bottom: 16px;">
              <div style="display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; background: rgb(var(--color-base-button) / 0.1); border: 2px solid rgb(var(--color-base-button) / 0.3); border-radius: var(--rounded-button);">
                ${this.getCheckCircleIconSVG()}
                <span style="font-weight: 700; font-size: 0.95rem; color: rgb(var(--color-base-button));">
                  3 Key Benefits
                </span>
              </div>
            </div>

            <!-- Quick Preview -->
            <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px;">
              <div style="display: flex; align-items: start; gap: 8px;">
                ${this.getCheckIconSVG()}
                <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.9);">
                  Time to inspect and fix any issues
                </span>
              </div>
              <div style="display: flex; align-items: start; gap: 8px;">
                ${this.getCheckIconSVG()}
                <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.9);">
                  No last-minute stress
                </span>
              </div>
              <div style="display: flex; align-items: start; gap: 8px;">
                ${this.getCheckIconSVG()}
                <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.9);">
                  Practical storage solutions included
                </span>
              </div>
            </div>

            <!-- Learn More Button -->
            <div style="display: flex; justify-content: flex-end;">
              <button
                type="button"
                class="pw-learn-more"
                aria-controls="${this.modalId}"
                aria-expanded="false"
              >
                <span>${this.content.viewButton}</span>
                <svg class="pw-learn-more__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>

          </div>
        </div>
      `;
    }

    /**
     * Build modal HTML using x-modal pattern (matches FAQ/Support structure)
     */
    buildModalHTML() {
      return `
        <x-modal id="${this.modalId}" class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="Delivery Guarantee Information" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${this.modalId}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <span class="drawer__title heading font-bold title-md">${this.content.modalHeading}</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="${this.modalId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 14" fill="none">
                    <path d="M15 1.00012L1 13.0001M1 1L15 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>
            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink">

                <!-- Hero Section -->
                <div class="text-center">
                  <div style="display: inline-flex; align-items: center; justify-content: center; width: 4rem; height: 4rem; background: rgb(var(--color-base-button)); border-radius: var(--rounded-card); margin-bottom: 1rem;">
                    ${this.getDeliveryIconSVG('var(--color-base-button-text)')}
                  </div>
                  <h3 class="heading font-bold text-xl md:text-2xl" style="margin-bottom: 0.75rem;">
                    ${this.content.modalHeading}
                  </h3>
                  <p class="text-base text-opacity" style="max-width: 500px; margin: 0 auto;">
                    ${this.content.modalSubheading}
                  </p>
                </div>

                <!-- Why Before Christmas Section -->
                <div>
                  <h4 class="heading font-bold text-lg" style="margin-bottom: 1rem;">
                    Why We Deliver Before Christmas
                  </h4>
                  <div style="display: flex; flex-direction: column; gap: 1rem;">

                    <div style="padding: 1rem; background: rgb(var(--color-drawer-background)); border: 2px solid rgb(var(--color-drawer-text) / 0.12); border-radius: var(--rounded-card);">
                      <div style="display: flex; align-items: start; gap: 0.75rem;">
                        <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-base-button) / 0.1); border-radius: var(--rounded-button);">
                          ${this.getCheckCircleIconSVG()}
                        </div>
                        <div style="flex: 1;">
                          <h5 style="font-size: 0.9375rem; font-weight: 600; margin: 0 0 0.25rem 0; color: rgb(var(--color-drawer-text));">
                            Quality Assurance
                          </h5>
                          <p style="font-size: 0.8125rem; margin: 0; color: rgb(var(--color-drawer-text) / 0.7); line-height: 1.5;">
                            Inspect your ride-on when it arrives. If anything isn't perfect, we have time to fix it or send a replacement before Christmas Eve.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div style="padding: 1rem; background: rgb(var(--color-drawer-background)); border: 2px solid rgb(var(--color-drawer-text) / 0.12); border-radius: var(--rounded-card);">
                      <div style="display: flex; align-items: start; gap: 0.75rem;">
                        <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-base-button) / 0.1); border-radius: var(--rounded-button);">
                          ${this.getClockIconSVG()}
                        </div>
                        <div style="flex: 1;">
                          <h5 style="font-size: 0.9375rem; font-weight: 600; margin: 0 0 0.25rem 0; color: rgb(var(--color-drawer-text));">
                            No Last-Minute Stress
                          </h5>
                          <p style="font-size: 0.8125rem; margin: 0; color: rgb(var(--color-drawer-text) / 0.7); line-height: 1.5;">
                            Avoid the anxiety of waiting for delivery on Christmas Eve. Your ride-on arrives safely with plenty of time to spare.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div style="padding: 1rem; background: rgb(var(--color-drawer-background)); border: 2px solid rgb(var(--color-drawer-text) / 0.12); border-radius: var(--rounded-card);">
                      <div style="display: flex; align-items: start; gap: 0.75rem;">
                        <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-base-button) / 0.1); border-radius: var(--rounded-button);">
                          ${this.getShieldIconSVG()}
                        </div>
                        <div style="flex: 1;">
                          <h5 style="font-size: 0.9375rem; font-weight: 600; margin: 0 0 0.25rem 0; color: rgb(var(--color-drawer-text));">
                            We Refuse to Disappoint
                          </h5>
                          <p style="font-size: 0.8125rem; margin: 0; color: rgb(var(--color-drawer-text) / 0.7); line-height: 1.5;">
                            Christmas magic shouldn't come with risk. If something goes wrong after Christmas, we can't fix it in time. Early delivery protects the magic.
                          </p>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                <!-- Storage Solutions Section -->
                <div style="padding: 1.25rem; background: rgb(var(--color-drawer-background)); border: 2px solid rgb(var(--color-drawer-text) / 0.12); border-radius: var(--rounded-card);">
                  <h4 class="heading font-bold text-lg" style="margin-bottom: 0.75rem;">
                    Worried About Hiding a Large Ride-On?
                  </h4>
                  <p class="text-sm text-opacity" style="margin-bottom: 1rem; line-height: 1.6;">
                    We understand - ride-on toys are BIG! Here are proven storage solutions from thousands of parents:
                  </p>

                  <div style="display: flex; flex-direction: column; gap: 0.75rem;">
                    <div style="display: flex; align-items: start; gap: 0.5rem;">
                      <span style="font-weight: 700; color: rgb(var(--color-base-button)); flex-shrink: 0;">1.</span>
                      <span class="text-sm" style="color: rgb(var(--color-drawer-text) / 0.85); line-height: 1.6;">
                        <strong>Garage or shed</strong> - Lock it away or cover with a tarp labelled "garden equipment"
                      </span>
                    </div>
                    <div style="display: flex; align-items: start; gap: 0.5rem;">
                      <span style="font-weight: 700; color: rgb(var(--color-base-button)); flex-shrink: 0;">2.</span>
                      <span class="text-sm" style="color: rgb(var(--color-drawer-text) / 0.85); line-height: 1.6;">
                        <strong>Car boot or spare vehicle</strong> - Perfect size and completely inaccessible to children
                      </span>
                    </div>
                    <div style="display: flex; align-items: start; gap: 0.5rem;">
                      <span style="font-weight: 700; color: rgb(var(--color-base-button)); flex-shrink: 0;">3.</span>
                      <span class="text-sm" style="color: rgb(var(--color-drawer-text) / 0.85); line-height: 1.6;">
                        <strong>At work</strong> - Many parents keep it in their office or workplace storage until Christmas Eve
                      </span>
                    </div>
                    <div style="display: flex; align-items: start; gap: 0.5rem;">
                      <span style="font-weight: 700; color: rgb(var(--color-base-button)); flex-shrink: 0;">4.</span>
                      <span class="text-sm" style="color: rgb(var(--color-drawer-text) / 0.85); line-height: 1.6;">
                        <strong>Grandparents or trusted friend's house</strong> - Ask them to store it for you
                      </span>
                    </div>
                    <div style="display: flex; align-items: start; gap: 0.5rem;">
                      <span style="font-weight: 700; color: rgb(var(--color-base-button)); flex-shrink: 0;">5.</span>
                      <span class="text-sm" style="color: rgb(var(--color-drawer-text) / 0.85); line-height: 1.6;">
                        <strong>Under a large sheet/dust cover</strong> - In a locked room or loft, cover it completely
                      </span>
                    </div>
                    <div style="display: flex; align-items: start; gap: 0.5rem;">
                      <span style="font-weight: 700; color: rgb(var(--color-base-button)); flex-shrink: 0;">6.</span>
                      <span class="text-sm" style="color: rgb(var(--color-drawer-text) / 0.85); line-height: 1.6;">
                        <strong>Neighbour with garage space</strong> - Many neighbours happy to help keep the magic alive!
                      </span>
                    </div>
                  </div>

                  <div style="margin-top: 1rem; padding: 0.75rem; background: white; border-radius: var(--rounded-button);">
                    <p class="text-sm" style="margin: 0; color: rgb(var(--color-drawer-text) / 0.7); line-height: 1.5; font-style: italic;">
                      <strong>Pro tip:</strong> Many customers arrange pickup by a friend/relative on delivery day so it never enters your home until Christmas Eve!
                    </p>
                  </div>
                </div>

                <!-- Our Promise Section -->
                <div>
                  <h4 class="heading font-bold text-lg" style="margin-bottom: 1rem;">
                    Our Delivery Promise
                  </h4>
                  <p class="text-base text-opacity" style="margin-bottom: 1rem; line-height: 1.6;">
                    We'd rather give you time to ensure perfection than risk disappointing your child with a last-minute issue we can't resolve.
                  </p>
                  <div style="padding: 1rem; background: rgb(var(--color-drawer-background)); border-left: 3px solid rgb(var(--color-base-button)); border-radius: var(--rounded-button);">
                    <p class="text-sm" style="margin: 0; color: rgb(var(--color-drawer-text) / 0.85); line-height: 1.6;">
                      <strong>Peace of mind guaranteed:</strong> Early delivery means you can contact us immediately if anything needs attention. Post-Christmas delivery leaves no time for corrections.
                    </p>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </x-modal>
      `;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
      // Make entire card clickable
      const cardElement = this.container.querySelector('[data-modal-trigger]');
      if (cardElement) {
        cardElement.addEventListener('click', (e) => {
          // Don't trigger if clicking the button directly (it has its own handler)
          if (e.target.closest('.pw-learn-more')) {
            return;
          }
          this.openModal();
        });
      }
    }

    /**
     * Open modal
     */
    openModal() {
      const modal = document.getElementById(this.modalId);
      if (modal && modal.show) {
        modal.show();
      }
    }

    /**
     * SVG Icons (inline for widget)
     * Uses currentColor pattern to support CSS variables properly
     */
    getDeliveryIconSVG(colorRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: 24px; height: 24px; color: rgb(${colorRGB});" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
          <path d="M9 11l3 3L22 4"></path>
          <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
        </svg>
      `;
    }

    getCheckCircleIconSVG(colorRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: 18px; height: 18px; color: rgb(${colorRGB});" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
      `;
    }

    getCheckIconSVG() {
      return `
        <svg style="width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    getClockIconSVG(colorRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: 18px; height: 18px; color: rgb(${colorRGB});" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
      `;
    }

    getShieldIconSVG(colorRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: 18px; height: 18px; color: rgb(${colorRGB});" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        </svg>
      `;
    }

    /**
     * Cleanup
     */
    destroy() {
      this.container.innerHTML = '';
    }
  }

  // Register widget in ProductWidgets namespace
  if (!window.ProductWidgets) {
    window.ProductWidgets = {};
  }
  if (!window.ProductWidgets.widgets) {
    window.ProductWidgets.widgets = {};
  }
  window.ProductWidgets.widgets.DeliveryGuaranteeWidget = DeliveryGuaranteeWidget;

})(window);
